/**
 * KnowledgeReportDetail.jsx
 * ------------------------------------------------------------------
 * "Report overview + Download" screen - the React equivalent of the PHP
 * publication-details.php page. NO member verification - direct download
 * for every report (member/OTP logic removed on request).
 *
 * Route:  /knowledge/reports/:slug
 *
 * Flow:
 *   1. Fetches one report from  api/knowledge-reports-detail.php?slug=:slug
 *   2. Shows  OVERVIEW  (cover image, title, publish date, long description)
 *   3. Shows  DOWNLOAD  button (direct link, opens in a new tab)
 *
 * Place this file at:  src/KnowledgeCenter/KnowledgeReportDetail.jsx
 * Route it with:       <Route path="/knowledge/reports/:slug" element={<KnowledgeReportDetail />} />
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, Download, FileSearch2, Home, Loader2 } from "lucide-react";

const API_BASE = "https://www.assocham.org/api"; // TODO: your API base URL

/* ------------------------------------------------------------------ */
/*  Breadcrumb                                                         */
/* ------------------------------------------------------------------ */
function Breadcrumb({ title }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="border-b border-black/5 bg-white/60 backdrop-blur-sm"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-hidden px-4 py-3 text-xs font-bold uppercase tracking-wide text-black/40 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex shrink-0 items-center gap-1.5 transition-colors hover:text-[#DA2128]">
          <Home className="h-3.5 w-3.5" /> Home
        </Link>
        <span className="shrink-0 text-black/25">›</span>
        <Link to="/knowledge" className="shrink-0 transition-colors hover:text-[#DA2128]">Knowledge</Link>
        <span className="shrink-0 text-black/25">›</span>
        <Link to="/knowledge/reports" className="shrink-0 transition-colors hover:text-[#DA2128]">Reports</Link>
        <span className="shrink-0 text-black/25">›</span>
        <span className="truncate rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm">
          {title}
        </span>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/*  Loading / Error screens                                            */
/* ------------------------------------------------------------------ */
function LoadingScreen() {
  return (
    <div className="grid min-h-[60vh] place-items-center bg-paper">
      <div className="flex flex-col items-center gap-3 text-mute">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
        <p className="text-sm font-semibold">Loading report…</p>
      </div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="grid min-h-[60vh] place-items-center bg-paper px-4">
      <div className="max-w-md rounded-2xl bg-white p-8 text-center ring-1 ring-brand/10 shadow-[0_10px_40px_-24px_rgba(180,35,24,0.35)]">
        <FileSearch2 className="mx-auto h-10 w-10 text-brand" />
        <h2 className="mt-4 font-display text-xl font-bold text-ink">Report not found</h2>
        <p className="mt-2 text-sm text-mute">{message || "This report does not exist or has been removed."}</p>
        <Link
          to="/knowledge/reports"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-crimson to-deeprose px-6 py-3 text-[13px] font-extrabold text-white shadow-lg shadow-brand/30 transition-all hover:shadow-brand/50"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Reports
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */
export default function KnowledgeReportDetail() {
  const { slug: param } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  // If the URL param is missing or literally "undefined" (old code navigated
  // with a field that didn't exist), don't even hit the API - show a clear message.
  const badParam = !param || param === "undefined" || param === "null";

  useEffect(() => {
    if (badParam) {
      setLoading(false);
      setError("Invalid report link. Please go back and click a report from the list.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);

    // Send both slug and id - the API accepts either one (in your data id == slug).
    fetch(`${API_BASE}/knowledge-reports-detail.php?slug=${encodeURIComponent(param)}&id=${encodeURIComponent(param)}`)
      .then((res) => {
        if (!res.ok) throw new Error("network");
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        if (json.success && json.data) {
          setDetail(json.data);
        } else {
          setError(json.message || "Report not found.");
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load report details.");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [param, badParam]);

  if (loading) return <LoadingScreen />;
  if (error || !detail) return <ErrorScreen message={error} />;

  return (
    <div className="min-h-screen bg-paper">
      <Breadcrumb title={detail.title} />

      <section className="mx-auto max-w-7xl px-4 pb-24 pt-8 sm:px-6 lg:px-8">
        <Link
          to="/knowledge/reports"
          className="inline-flex items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wider text-brand transition-colors hover:text-deeprose"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Reports
        </Link>

        <div className="mt-8 grid gap-10 lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-14">
          {/* -------- left: cover card -------- */}
          <div>
            <div className="relative overflow-hidden rounded-2xl bg-white shadow-[0_18px_50px_-24px_rgba(20,20,25,0.35)] ring-1 ring-black/5">
              <img
                src={detail.img}
                alt={detail.title}
                className="aspect-[3/4] w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <p className="mt-3 text-center text-[13px] font-semibold text-mute">{detail.date}</p>
          </div>

          {/* -------- right: overview + download -------- */}
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-ember">
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-ember" />
              Report Overview
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl">
              {detail.title}
            </h1>
            <p className="mt-3 text-sm font-semibold text-mute">{detail.date}</p>

            {/* long_description comes as HTML from the CMS - same as the PHP page renders it */}
            <div
              className="mt-6 space-y-4 text-[15px] leading-relaxed text-ink/80 [&_p]:my-3 [&_h2]:font-bold [&_h2]:text-ink [&_h3]:font-bold [&_h3]:text-ink [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1 [&_a]:text-brand [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: detail.long_description }}
            />

            {/* -------- direct download - no member verification -------- */}
            <div className="mt-10 border-t-2 border-ink/5 pt-8">
              <h2 className="font-display text-lg font-bold text-ink">Download this report</h2>
              <p className="mt-1 text-[13.5px] text-mute">
                Click the button below to download this report.
              </p>
              <div className="mt-5">
                {detail.download_url ? (
                  <a
                    href={detail.download_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-crimson to-deeprose px-7 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-brand/30 transition-all hover:shadow-brand/50"
                  >
                    <Download className="h-4 w-4" /> Download
                  </a>
                ) : (
                  <p className="text-[13px] font-semibold text-mute">
                    Download link not available yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
